import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiDocumentServiceRepository } from '../../services/repositories/implementations/ApiDocumentServiceRepository';
import { trackError } from '@proposify/frontend-observability';
import { QueryKeys } from 'utils/queryKeys';
import type { CompaniesControllerCreateCompanyMutationRequest, CompanyResponse } from '@proposify/document-service-api-schema';
import type { CompanyEntity } from '../../services/entities/CompanyEntity.ts';
import { FeatureFlags } from 'utils/featureFlags.ts';
import useFeatureFlag from 'hooks/useFeatureFlag.ts';
import { ApiCompanyAndContactsRepository } from 'services/company-and-contacts/ApiCompanyAndContactsRepository.ts';

export type CompanyFormData = CompaniesControllerCreateCompanyMutationRequest;

export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  const featureFlags = useFeatureFlag([FeatureFlags.useVariablesBff]);
  const isUseVariablesBffFlagEnabled = featureFlags[FeatureFlags.useVariablesBff];
  const repository = isUseVariablesBffFlagEnabled ? ApiCompanyAndContactsRepository : apiDocumentServiceRepository;

  return useMutation<CompanyResponse, Error, CompanyFormData>({
    mutationFn: async (companyData) => {
      try {
        return await repository.createCompany(companyData);
      } catch (error) {
        trackError(error, {
          action: 'createCompany',
          repository: isUseVariablesBffFlagEnabled ? 'ApiCompanyAndContactsRepository' : 'ApiDocumentServiceRepository',
        });
        throw error;
      }
    },
    onSuccess: (newCompany) => {
      queryClient.setQueryData(QueryKeys.getCompanies, (old: CompanyEntity[] = []) => [...old, newCompany]);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.getCompanies });
    },
  });
};
